import type { TokenResponse } from "@getgreenline/sso";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { z } from "zod";

interface AuthUser {
  email: string;
  name: string;
}

interface AuthState {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
  realm: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setTokens: (response: TokenResponse) => void;
  clearTokens: () => void;
}

const jwtUserClaimsSchema = z.object({
  email: z.string().default(""),
  name: z.string().optional(),
  given_name: z.string().optional(),
  family_name: z.string().optional(),
  preferred_username: z.string().optional(),
});

function decodeJwtPayload(token: string): unknown {
  const parts = token.split(".");
  const base64 = parts[1] ?? "";
  const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json);
}

function extractUser(rawPayload: unknown): AuthUser {
  const claims = jwtUserClaimsSchema.parse(rawPayload);

  const name =
    claims.given_name && claims.family_name
      ? `${claims.given_name} ${claims.family_name}`
      : (claims.name ?? claims.preferred_username ?? claims.email);

  return { email: claims.email, name };
}

const initialState = {
  accessToken: null,
  idToken: null,
  refreshToken: null,
  realm: null,
  expiresAt: null,
  user: null,
  isAuthenticated: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,
      setTokens: (response: TokenResponse) => {
        const payload = decodeJwtPayload(response.access_token);
        const user = extractUser(payload);
        const expiresAt = Date.now() + response.expires_in * 1000;

        set({
          accessToken: response.access_token,
          idToken: response.id_token,
          refreshToken: response.refresh_token,
          realm: response.realm,
          expiresAt,
          user,
          isAuthenticated: true,
        });
      },
      clearTokens: () => set(initialState),
    }),
    {
      name: "blaze-ads-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        idToken: state.idToken,
        refreshToken: state.refreshToken,
        realm: state.realm,
        expiresAt: state.expiresAt,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export function waitForHydration(): Promise<void> {
  return new Promise((resolve) => {
    if (useAuthStore.persist.hasHydrated()) {
      resolve();
      return;
    }
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      unsubscribe();
      resolve();
    });
  });
}
